// ======================================================
// ✅ Contexto: Tema institucional (claro / oscuro)
// Autor: Breyner Javier Montalvo
// ------------------------------------------------------
// Sistema visual persistente, accesible y sincronizado
// con el sistema operativo (prefers-color-scheme).
// Coherente con Variables.css y React 18.
// ======================================================

import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from "react";

const ThemeContext = createContext(null);

/* ======================================================
   🌤 Proveedor de Tema
   ====================================================== */
export function ThemeProvider({ children }) {
  // 🧭 Determina el tema inicial (localStorage o sistema)
  const obtenerTemaInicial = useCallback(() => {
    try {
      const guardado = localStorage.getItem("tema");
      if (guardado === "claro" || guardado === "oscuro") {
        return guardado;
      }

      if (typeof window !== "undefined") {
        const prefiereOscuro = window.matchMedia(
          "(prefers-color-scheme: dark)"
        ).matches;
        return prefiereOscuro ? "oscuro" : "claro";
      }

      return "claro";
    } catch {
      return "claro";
    }
  }, []);

  const [tema, setTema] = useState(obtenerTemaInicial);

  // 🧩 Determina si se puede usar useLayoutEffect (SSR safe)
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect;

  /* ======================================================
     🧱 Aplicar atributo y persistir localmente
     ====================================================== */
  useIsomorphicLayoutEffect(() => {
    try {
      const html = document.documentElement;
      const body = document.body;
      if (!html || !body) {return;}

      // Asigna el atributo al nivel más alto
      html.setAttribute("data-tema", tema);
      body.setAttribute("data-tema", tema);
      localStorage.setItem("tema", tema);

      // Transición suave al cambiar tema
      html.style.transition = "background-color 0.4s ease, color 0.4s ease";
      body.style.transition = "background-color 0.4s ease, color 0.4s ease";

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.info(`🎨 Tema activo → ${tema.toUpperCase()}`);
      }
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("⚠ No se pudo aplicar el tema:", err);
      }
    }
  }, [tema]);

  /* ======================================================
     🔄 Sincronizar con el sistema operativo
     ====================================================== */
  useEffect(() => {
    if (typeof window === "undefined") {return;}

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      const nuevoTema = e.matches ? "oscuro" : "claro";
      const guardado = localStorage.getItem("tema");
      if (!guardado) {
        setTema(nuevoTema);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  /* ======================================================
     🌓 Alternar tema manualmente
     ====================================================== */
  const alternarTema = useCallback(() => {
    setTema((prev) => (prev === "claro" ? "oscuro" : "claro"));
  }, []);

  /* ======================================================
     🧩 Memorizar valores para evitar renders innecesarios
     ====================================================== */
  const valorContexto = useMemo(
    () => ({ tema, alternarTema }),
    [tema, alternarTema]
  );

  return (
    <ThemeContext.Provider value={valorContexto}>
      {children}
    </ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/* ======================================================
   🎯 Hook personalizado – useTema()
   ====================================================== */
export function useTema() {
  const contexto = useContext(ThemeContext);
  if (!contexto) {
    throw new Error("❌ useTema debe usarse dentro de un <ThemeProvider>");
  }
  return contexto;
}
